import { useEffect } from 'react';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { CountryBar } from 'src/components/country-bar';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { setCampaignLeaksRegions } from 'src/context/reducers/filter-settings/filterSlice';
import { CountryTabLabel } from '../explore/CountryTabLabel';
import { useCampaignLeaksPlatform } from './shared/platform-toggle';
import { useCampaignLeaksRegions } from './shared/regions';

/**
 * Parent route of both Campaign Leaks tabs: the region bar of the Traffic page, mounted once so the selection
 * is kept when switching tabs. "All" on every arrival on the page and on workspace / platform change; no URL
 * or localStorage restore (the Traffic selection is not shared).
 */
export default function CampaignLeaksLayout() {
  const dispatch = useDispatch();
  const workspace: any = useCurrentWorkspace();
  const platform = useCampaignLeaksPlatform();
  const { regions, allNames, selected, loading } = useCampaignLeaksRegions();

  // Leaving the page forgets the selection, so the next arrival starts on "All".
  useEffect(() => () => {
    dispatch(setCampaignLeaksRegions([]));
  }, [dispatch]);

  // Keyed on the names, not on the array: a refetch returning the same regions keeps the selection.
  const regionsKey = allNames.join('\n');
  useEffect(() => {
    dispatch(setCampaignLeaksRegions(regionsKey ? regionsKey.split('\n') : []));
  }, [workspace?.id, platform, regionsKey, dispatch]);

  return (
    <>
      <Box px="24px">
        <CountryBar
          countries={regions}
          multiple
          selectedCountries={selected.length ? selected : allNames}
          onCountriesChange={(names) => dispatch(setCampaignLeaksRegions(names))}
          loading={loading}
          // Header of two rows (title + tabs, no filter row unlike Traffic): same offsets as the Brand Shield pages.
          stickyTop={107}
          backgroundColor="background.paper"
          topGap={20}
          reserveGapWhenHidden
          contentPaddingX="24px"
          tabSpacing={12}
          renderCountryLabel={(country, isSelected) => (
            <CountryTabLabel country={country} isSelected={isSelected} />
          )}
        />
      </Box>
      {/* 24px under the visible bar, as on the Brand Shield pages; none when it is hidden (single region). */}
      <Box sx={{ mt: loading || regions.length > 1 ? '24px' : 0 }}>
        <Outlet />
      </Box>
    </>
  );
}
